/*
 * censor-project.js — a censoring project as ONE file on the person's device.
 *
 * Chat 640 (Ангелина, 08.10.2026): «как будто надо делать проекты, чтобы
 * сохранялись, чтобы могла спокойно в любой момент перейти, а не загружать
 * исходник каждый раз». Her work lived only in this browser's storage (one
 * eviction had already taken two hours of it, 27.09), and only the latest
 * project was named on the page. Farid's call: the project is kept on HER
 * device, not on our server — the recordings are hers.
 *
 * Layout of a .tmbcensor file:
 *     "TMBCENSOR1"            10 bytes, ASCII
 *     json length             4 bytes, uint32 little-endian
 *     JSON (UTF-8)            the transcript, every decision, the settings,
 *                             the word lists, the replacement sound (small)
 *     media bytes (optional)  the recording itself — when saved «with it»
 *
 * The media is never copied in memory: saving composes a Blob of references
 * to the original File, opening slices the project file. A 769 MB video costs
 * the tab nothing more than the header.
 *
 * Pure (no DOM): unit-tested by tests/js/profanity-censor-project.test.mjs.
 */
(function (root) {
    'use strict';

    var MAGIC = 'TMBCENSOR1';
    var FORMAT = 'timbrica-censor-project';
    var VERSION = 1;
    var EXT = 'tmbcensor';
    var HEAD = MAGIC.length + 4;
    var MAX_JSON = 256 * 1048576;      // a three-hour transcript is ~5 MB; anything near this is not ours

    function utf8(str) { return new TextEncoder().encode(str); }

    /* data — the project object (see makeProject); media — File|Blob|null.
       Returns a Blob: header + JSON + (media by reference). */
    function encode(data, media) {
        var json = utf8(JSON.stringify(data));
        var head = new Uint8Array(HEAD);
        for (var i = 0; i < MAGIC.length; i++) head[i] = MAGIC.charCodeAt(i);
        new DataView(head.buffer).setUint32(MAGIC.length, json.length, true);
        var parts = [head, json];
        if (media) parts.push(media);
        return new Blob(parts, { type: 'application/octet-stream' });
    }

    /* file — the picked project file (File/Blob). Resolves to
       { data, media } where media is a Blob slice of the file (or null), or
       rejects with an Error whose `code` names what is wrong:
         NOT_A_PROJECT — another file (a recording, a document);
         DAMAGED       — the header or the JSON does not hold;
         TOO_NEW       — written by a newer version of the tool. */
    function decode(file) {
        function fail(code) { var e = new Error(code); e.code = code; return Promise.reject(e); }
        if (!file || !(file.size >= HEAD)) return fail('NOT_A_PROJECT');
        return file.slice(0, HEAD).arrayBuffer().then(function (ab) {
            var u = new Uint8Array(ab), magic = '';
            for (var i = 0; i < MAGIC.length; i++) magic += String.fromCharCode(u[i]);
            if (magic !== MAGIC) return fail('NOT_A_PROJECT');
            var len = new DataView(ab).getUint32(MAGIC.length, true);
            if (!(len > 1) || len > MAX_JSON || HEAD + len > file.size) return fail('DAMAGED');
            return file.slice(HEAD, HEAD + len).text().then(function (txt) {
                var data;
                try { data = JSON.parse(txt); } catch (e) { return fail('DAMAGED'); }
                if (!data || data.format !== FORMAT) return fail('DAMAGED');
                if ((data.v | 0) > VERSION) return fail('TOO_NEW');
                if (!Array.isArray(data.words) || !data.file) return fail('DAMAGED');
                var media = null;
                if (data.media && file.size > HEAD + len) {
                    media = file.slice(HEAD + len, file.size, (data.file && data.file.type) || '');
                    if (data.file.size && media.size !== data.file.size) return fail('DAMAGED');
                }
                return { data: data, media: media };
            });
        });
    }

    /* The project object from the editor's state — one shape for the file
       and for the browser's own store, so either can open the other. */
    function makeProject(s) {
        return {
            format: FORMAT, v: VERSION, app: s.app || 'profanity-remover',
            savedAt: new Date().toISOString(),
            file: { name: s.fileName || '', size: s.fileSize || 0, type: s.fileType || '', lastModified: s.fileModified || 0, duration: s.duration || 0 },
            lang: s.lang || '', engine: s.engine || '',
            words: s.words || [],
            o: s.o || {}, m: s.m || [], e: s.e || {}, s: s.s || 1,
            settings: s.settings || {},
            custom: s.custom || null,
            media: !!s.media
        };
    }

    /* How the editor's restore reads a project: the saved-session shape. */
    function toSession(p) {
        var n = Object.keys(p.o || {}).length + (p.m || []).length + Object.keys(p.e || {}).length;
        return {
            origin: 'profanity-censor', status: 'done', slug: p.app,
            fileName: p.file.name, fileSize: p.file.size, duration: p.file.duration,
            lang: p.lang, engine: p.engine, words: p.words, wordCount: p.words.length,
            o: p.o || {}, m: p.m || [], e: p.e || {}, s: p.s || 1,
            decisions: n, startedAt: Date.parse(p.savedAt) || Date.now(), fromProject: true
        };
    }

    /* «Нарезка 3.mp3» + «проект» → «Нарезка 3 — проект» (the page adds the
       date and the extension through the shared project door). */
    function baseName(mediaName, label) {
        var base = String(mediaName || 'project').replace(/\.[^.]+$/, '');
        return base + ' — ' + (label || 'project');
    }

    var api = { MAGIC: MAGIC, FORMAT: FORMAT, VERSION: VERSION, EXT: EXT, HEAD: HEAD,
        encode: encode, decode: decode, makeProject: makeProject, toSession: toSession, baseName: baseName };
    root.ProfanityCensorProject = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : window);
